import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, reactive, ref, type Ref } from 'vue';
import CModal from '@/components/CModal.vue';
import CButton from '@/components/shared/CButton';
import CButtonGroup, { type CButtonGroupItem } from '@/components/shared/CButtonGroup';
import CFileInput, { type CFileInputRef } from '@/components/shared/CFileInput';
import CPagination from '@/components/shared/CPagination';
import CPreview from '@/components/shared/CPreview.vue';
import { getDefaultMasks, getUserMasks } from '@/api/masks';
import type { MaskDto } from '@/api/typings/mask';
import { MaskType } from '@/utils/enums/maskType.enum';
import {
    MASK_UPLOAD_ACCEPT,
    RASTER_MASK_UPLOAD_ACCEPT,
    isSvgMask,
    uploadAndRegisterMask
} from '@/utils/maskUtils';

type MaskSource = 'customily' | 'uploaded';


export default /*#__PURE__*/_defineComponent({
  __name: 'MaskSelectorModal',
  props: {
    type: { default: MaskType.Mask },
    disableSvg: { type: Boolean },
    multiple: { type: Boolean },
    presets: null,
    title: null,
    accept: null,
    maxKbSize: null
  },
  emits: ["select", "select-multiple", "cancel", "error"],
  setup(__props: any, { emit }: { emit: ({
    (event: 'select', mask: MaskDto): void;
    (event: 'select-multiple', masks: MaskDto[]): void;
    (event: 'cancel'): void;
    (event: 'error', message: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { type: MaskType, disableSvg?: boolean, multiple?: boolean, presets?: MaskDto[], title?: string, accept?: string, maxKbSize?: number };

const PAGE_SIZE = 35;





const sources: CButtonGroupItem<MaskSource>[] = [
    { label: 'Customily', value: 'customily' },
    { label: 'Uploaded', value: 'uploaded' }
];

const model = reactive({
    filters: [] as MaskSource[],
    defaultMasks: [] as MaskDto[],
    userMasks: [] as MaskDto[],
    userMasksTotal: 0,
    page: 1,
    loading: false,
    uploading: false,
    // Kept as DTOs in pick order, so the selection survives page and filter changes
    selected: [] as MaskDto[]
});

const fileInputRef: Ref<CFileInputRef | undefined> = ref();

const accept = computed(
    () => props.accept ?? (props.disableSvg ? RASTER_MASK_UPLOAD_ACCEPT : MASK_UPLOAD_ACCEPT)
);
// Filters are inclusive, so none picked and both picked mean the same thing: show everything
const isFiltered = (source: MaskSource) => !model.filters.length || model.filters.includes(source);
const showUserMasks = computed(() => isFiltered('uploaded'));
const showDefaultMasks = computed(() => isFiltered('customily'));

const userMasksCount = computed(() => (showUserMasks.value ? model.userMasksTotal : 0));
const totalMaskCount = computed(
    () => userMasksCount.value + (showDefaultMasks.value ? model.defaultMasks.length : 0)
);

// Unfiltered it is one flat list of the user's masks followed by the Customily ones: the defaults
// start filling the page where the uploaded ones run out and spill onto the next ones
const masks = computed(() => {
    const userMasks = showUserMasks.value ? model.userMasks : [];
    if (!showDefaultMasks.value) return userMasks;

    const firstDefault = Math.max(0, (model.page - 1) * PAGE_SIZE - userMasksCount.value);
    return [
        ...userMasks,
        ...model.defaultMasks.slice(firstDefault, firstDefault + PAGE_SIZE - userMasks.length)
    ];
});
const isEmpty = computed(() => !model.loading && !masks.value.length);
const emptyMessage = computed(() => {
    if (!showDefaultMasks.value) {
        return "You haven't uploaded any shape yet. Upload one and it will be available on every template.";
    }
    if (!showUserMasks.value) return 'There are no Customily shapes available yet.';
    return 'There are no shapes available yet. Upload one and it will be available on every template.';
});

const loadDefaultMasks = async () => {
    model.defaultMasks = props.presets ?? (await getDefaultMasks(props.type));
};

const loadUserMasks = async (page: number) => {
    const { masks: userMasks, totalCount } = await getUserMasks(page, PAGE_SIZE, props.type);
    model.userMasks = userMasks;
    model.userMasksTotal = totalCount;
    model.page = page;
};

const withLoading = async (loading: Promise<unknown>) => {
    model.loading = true;
    try {
        await loading;
    } finally {
        model.loading = false;
    }
};

const onPageChange = (page: number) => withLoading(loadUserMasks(page));

const onFiltersChange = (filters: MaskSource[]) => {
    model.filters = filters;
    if (model.page !== 1) onPageChange(1);
};

const isDisabled = (mask: MaskDto) => props.disableSvg && isSvgMask(mask.path);

const selectionOrder = (mask: MaskDto) => model.selected.findIndex((selected) => selected.id === mask.id) + 1;

const toggleSelection = (mask: MaskDto) => {
    const index = selectionOrder(mask) - 1;
    if (index >= 0) {
        model.selected.splice(index, 1);
    } else {
        model.selected.push(mask);
    }
};

const onMaskSelected = (mask: MaskDto) => {
    if (isDisabled(mask)) return;

    if (props.multiple) {
        toggleSelection(mask);
        return;
    }
    emit('select', mask);
};

const onConfirm = () => {
    if (!model.selected.length) return;
    emit('select-multiple', [...model.selected]);
};

const onUploadClicked = () => fileInputRef.value?.openFileUploader();

// The mask picker keeps the message it always had
const uploadFailed = (error: unknown) => {
    // A rejected registration says why (eg. a text shape with nothing visible), axios' own message doesn't
    const serverMessage = (error as { response?: { data?: { Message?: string } } } | undefined)?.response?.data?.Message;
    emit(
        'error',
        serverMessage ||
            (error as Error | undefined)?.message ||
            (props.type === MaskType.TextShape ? 'The shape could not be uploaded' : 'The mask could not be uploaded')
    );
};

const onFileUpload = async (files: File[]) => {
    if (!files?.length) return;

    const oversized = props.maxKbSize ? files.filter((file) => file.size > props.maxKbSize! * 1024) : [];
    if (oversized.length) {
        emit('error', `${oversized.map((file) => file.name).join(', ')} exceeds the ${props.maxKbSize}KB limit`);
        return;
    }

    model.uploading = true;
    try {
        if (!props.multiple) {
            emit('select', await uploadAndRegisterMask(files[0], props.type));
            return;
        }

        // One by one so a failing file keeps the ones already registered selected
        for (const file of files) {
            try {
                model.selected.push(await uploadAndRegisterMask(file, props.type));
            } catch (error) {
                uploadFailed(error);
            }
        }
        // The uploads already went through and stay selected, so a failed refresh isn't an upload error
        await withLoading(loadUserMasks(1)).catch((error) => {
            console.error(error);
            emit('error', 'The shapes were uploaded but the list could not be refreshed');
        });
    } catch (error) {
        uploadFailed(error);
    } finally {
        model.uploading = false;
    }
};

onMounted(() => withLoading(Promise.all([loadDefaultMasks(), loadUserMasks(1)])));

return { __sfc: true,PAGE_SIZE, props, emit, sources, model, fileInputRef, accept, isFiltered, showUserMasks, showDefaultMasks, userMasksCount, totalMaskCount, masks, isEmpty, emptyMessage, loadDefaultMasks, loadUserMasks, withLoading, onPageChange, onFiltersChange, isDisabled, selectionOrder, toggleSelection, onMaskSelected, onConfirm, onUploadClicked, uploadFailed, onFileUpload, CModal, CButton, CButtonGroup, CFileInput, CPagination, CPreview }
}

})